
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Save } from 'lucide-react';
import LessonEditor from '@/components/LessonEditor';
import { getDefaultLesson, Lesson } from '@/data/lessons';
import { getAllLessons, saveLesson } from '@/utils/lessonUtils';
import { toast } from "sonner";
import analytics from '@/utils/analytics';
import LessonList from './LessonList';

const ContentManagementTab = () => {
  const [allLessons, setAllLessons] = useState<Lesson[]>(getAllLessons());
  const [currentLesson, setCurrentLesson] = useState<Lesson>(getDefaultLesson());

  useEffect(() => {
    // Load the latest lessons
    setAllLessons(getAllLessons());
  }, []);

  const handleSaveLesson = (updatedLesson: Lesson) => {
    try {
      // Find if the lesson already exists
      const existingIndex = allLessons.findIndex(l => l.day === updatedLesson.day);
      
      let newLessons: Lesson[];
      
      if (existingIndex >= 0) {
        // Update existing lesson
        newLessons = [...allLessons];
        newLessons[existingIndex] = updatedLesson;
      } else {
        // Add new lesson
        newLessons = [...allLessons, updatedLesson];
      }
      
      // Sort lessons by day
      newLessons.sort((a, b) => a.day - b.day);
      
      // Save to localStorage via lessonUtils
      saveLesson(updatedLesson);
      
      setAllLessons(newLessons);
      setCurrentLesson(updatedLesson);
      
      toast.success(`Day ${updatedLesson.day} lesson saved successfully`);
      analytics.trackUserAction('admin_save_lesson', { day: updatedLesson.day });
      
    } catch (error) {
      console.error('Error saving lesson:', error);
      toast.error('Failed to save lesson. Please try again.');
    }
  };
  
  const createNewLesson = () => {
    // Find the next available day number
    const nextDay = allLessons.length > 0 
      ? Math.max(...allLessons.map(l => l.day)) + 1 
      : 1;
    
    // Create a new lesson template
    const newLesson: Lesson = {
      day: nextDay,
      title: `Day ${nextDay} Lesson`,
      subtitle: "New Lesson",
      videoId: "",
      content: ["Add your content here"],
      actionStep: {
        title: "Action Step",
        description: "Describe the action step here"
      },
      checkInQuestion: {
        type: "text",
        question: "How are you feeling today?"
      },
      points: 10
    };
    
    setCurrentLesson(newLesson);
    analytics.trackUserAction('admin_create_lesson', { day: nextDay });
  };

  return (
    <div className="flex flex-col md:flex-row gap-6">
      {/* Sidebar with lesson list */}
      <div className="w-full md:w-1/4">
        <LessonList 
          lessons={allLessons}
          currentLessonDay={currentLesson.day}
          onSelectLesson={setCurrentLesson}
          onCreateLesson={createNewLesson}
        />
      </div>
      
      {/* Main content with lesson editor */}
      <div className="w-full md:w-3/4">
        <div className="bg-white p-6 rounded-xl border border-hormony-beige shadow-sm">
          <h2 className="text-xl font-semibold mb-6 flex items-center justify-between">
            <span>Edit Lesson: Day {currentLesson.day}</span>
            <Button 
              variant="default" 
              size="sm"
              onClick={() => handleSaveLesson(currentLesson)}
            >
              <Save className="mr-1 h-4 w-4" />
              Save
            </Button>
          </h2>
          
          <LessonEditor 
            lesson={currentLesson} 
            onSave={handleSaveLesson} 
          />
        </div>
      </div>
    </div>
  );
};

export default ContentManagementTab;
